import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { message } from "antd";
import StickyFlexTable from "../common/StickyFlexTable";
import { useTheme } from "../../ThemeContext";
import {
  fetchCallsList,
  getFullMemberName,
  getCallStatusInfo,
  getCallOutcomeInfo,
  formatDateTime,
  formatDuration,
} from "../../services/call-history-service";
import CallDetailsPage from "./CallDetailsPage";
import "./CallHistoryPage.css";
import ManagementToolbar from "../common/ManagementToolbar";
import { SEARCH_PLACEHOLDERS, CALL_HISTORY_FILTER_GROUPS } from "../../constants/filter-constants";
import ThemedChip from "../common/ThemedChip";

/**
 * CallHistoryList - The actual list component (separated from routing logic)
 */
const CallHistoryList = () => {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params
  const batchId = searchParams.get("batch_id") ?? "";
  const initialPage = parseInt(searchParams.get("page") || "0", 10);
  const initialPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
  const initialQuery = searchParams.get("q") || "";
  const initialCallStatus = searchParams.get("call_status") || "";

  // State
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [callStatusFilter, setCallStatusFilter] = useState(initialCallStatus);
  
  // Refs for debouncing
  const debounceTimerRef = useRef(null);

  // Fetch calls function
  const loadCalls = useCallback(async (queryValue, statusValue) => {
    try {
      setLoading(true);
      const result = await fetchCallsList({
        batchId: batchId || undefined,
        page,
        rowsPerPage,
        searchQuery: queryValue,
        callStatus: statusValue,
      });

      setCalls(result.calls || []);
      setTotalCount(result.totalCount || 0);

      // Cache for navigation
      sessionStorage.setItem('callHistoryNavCache', JSON.stringify({
        calls: result.calls || [],
        page,
        rowsPerPage,
        totalCount: result.totalCount || 0,
        batchId,
        query: queryValue,
        callStatus: statusValue,
      }));
    } catch (error) {
      console.error("Error fetching calls:", error);
      setCalls([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, batchId]);

  // Initial load and when pagination changes
  useEffect(() => {
    loadCalls(searchQuery, callStatusFilter);
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search with debounce
  const handleSearchChange = (newQuery) => {
    setSearchQuery(newQuery);
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      setPage(0);
      const params = new URLSearchParams(searchParams);
      if (newQuery) {
        params.set('q', newQuery);
      } else {
        params.delete('q');
      }
      params.set('page', '0');
      setSearchParams(params, { replace: true });
      loadCalls(newQuery, callStatusFilter);
    }, 1000);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    const newStatus = newFilters.call_status || "";
    setCallStatusFilter(newStatus);
    setPage(0);
    
    const params = new URLSearchParams(searchParams);
    if (newStatus) {
      params.set("call_status", newStatus);
    } else {
      params.delete("call_status");
    }
    params.set("page", "0");
    setSearchParams(params, { replace: true });
    
    loadCalls(searchQuery, newStatus);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params, { replace: true });
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    params.set("page", "0");
    params.set("pageSize", newRowsPerPage.toString());
    setSearchParams(params, { replace: true });
  };

  // Handle row click
  const handleRowClick = (row) => {
    if (!row.callRequestId) return;

    if (row.callStatus === "cancelled") {
      message.error("This call has been cancelled and cannot be accessed.");
      return;
    }
    if (row.callStatus === "scheduled" || row.callStatus === "Active" || row.callStatus === "active") {
      message.warning("This call has been scheduled and cannot be accessed.");
      return;
    }
    if (row.callStatus === "pending") {
      message.warning("This call is pending and cannot be accessed yet.");
      return;
    }

    navigate(`/call-history?call_request_id=${row.callRequestId}&renderType=summary`);
  };

  // Get chip variant
  const getStatusChipVariant = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "cancelled":
      case "failure":
      case "failed":
        return "error";
      case "successful":
      case "success":
      case "completed":
        return "success";
      case "scheduled":
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Column definitions
  const columns = [
    {
      renderName: "memberName",
      headerName: "Member Name",
      minWidth: 180,
      fixed: true,
      sortable: false,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: currentTheme.text.primary }}>
          {getFullMemberName(row)}
        </Typography>
      ),
    },
    {
      renderName: "primaryMemberId",
      headerName: "Member ID",
      minWidth: 120,
      sortable: false,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ color: currentTheme.text.primary }}>
          {row.primaryMemberId || "—"}
        </Typography>
      ),
    },
    {
      renderName: "callStatus",
      headerName: "Status",
      minWidth: 100,
      sortable: false,
      align: "center",
      renderCell: (row) => {
        
        let status = getCallStatusInfo(row.callStatus).label;
        if (status === "active" || status === "Active") status = "Scheduled";

        return (
        <ThemedChip variant={getStatusChipVariant(status)} size="small">
          {getCallStatusInfo(status).label}
        </ThemedChip>
      )},
    },
    {
      renderName: "callOutcome",
      headerName: "Outcome",
      minWidth: 110,
      sortable: false,
      align: "center",
      renderCell: (row) => {
        if (!row.callOutcome) return <Typography variant="body2">—</Typography>;
        return (
          <ThemedChip variant={getStatusChipVariant(row.callOutcome)} size="small">
            {getCallOutcomeInfo(row.callOutcome).label}
          </ThemedChip>
        );
      },
    },
    // {
    //   renderName: "callStartTime",
    //   headerName: "Start Time",
    //   minWidth: 160,
    //   sortable: false,
    //   renderCell: (row) => (
    //     <Typography variant="body2" sx={{ color: currentTheme.text.primary }}>
    //       {formatDateTime(row.callStartTime)}
    //     </Typography>
    //   ),
    // },
    // {
    //   renderName: "callEndTime",
    //   headerName: "End Time",
    //   minWidth: 160,
    //   sortable: false,
    //   renderCell: (row) => (
    //     <Typography variant="body2" sx={{ color: currentTheme.text.primary }}>
    //       {formatDateTime(row.callEndTime)}
    //     </Typography>
    //   ),
    // },
    // {
    //   renderName: "totalDuration",
    //   headerName: "Duration",
    //   minWidth: 90,
    //   sortable: false,
    //   align: "center",
    //   renderCell: (row) => (
    //     <Typography variant="body2" sx={{ color: currentTheme.text.primary }}>
    //       {formatDuration(row.totalDuration)}
    //     </Typography>
    //   ),
    // },
    {
      renderName: "callDirection",
      headerName: "Direction",
      minWidth: 100,
      sortable: false,
      align: "center",
      renderCell: (row) => (
        <Typography variant="body2" sx={{ color: currentTheme.text.primary, textTransform: "capitalize" }}>
          {row.callDirection || "—"}
        </Typography>
      ),
    },
  ];

  return (
    <Box
      className="call-history-page-container"
      sx={{
        width: "100%",
        height: "calc(100vh - 80px)",
        backgroundColor: currentTheme.callhistory?.mainBackground || currentTheme.homePage?.mainBackground,
        padding: 3,
      }}
    >
      <StickyFlexTable
        columns={columns}
        rows={calls}
        loading={loading}
        isLoading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsCount={totalCount}
        rowSelector={handleRowClick}
        getRowKey={(row, idx) => row.callRequestId || row._id || idx}
        containerHeight="calc(100vh - 16.25rem)"
        noCustomizeColumn={true}
        toolbar={
          <ManagementToolbar
            module="call-history"
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            searchPlaceholder={SEARCH_PLACEHOLDERS.callHistory}
            showAddButton={false}
            searchBarWidth="110%"
            filterGroups={CALL_HISTORY_FILTER_GROUPS}
            selectedFilters={{ call_status: callStatusFilter }}
            onFiltersChange={handleFilterChange}
          />
        }
      />
    </Box>
  );
};

/**
 * CallHistoryPage - Router component that decides what to show
 */
const CallHistoryPage = () => {
  const [searchParams] = useSearchParams();
  const callRequestId = searchParams.get("call_request_id");

  console.log("CallHistoryPage render - callRequestId:", callRequestId);

  // Simple conditional: show details or list
  // Key forces clean remount when switching between views
  if (callRequestId) {
    console.log("Rendering CallDetailsPage");
    return <CallDetailsPage key={callRequestId} />;
  }

  console.log("Rendering CallHistoryList");
  return <CallHistoryList key="list" />;
};

export default CallHistoryPage;